import React from 'react';
import style from './airportTypeahead.module.scss';
import classNames from 'classnames';
import { connect } from 'react-redux';
import { Dispatch } from 'redux';
import { Airport } from 'acscp/store/airportsState';
import { PresentationComponentBase } from 'acscp/components/presentationComponentBase';
import { AppState } from 'acscp/store';
import { ClearAirportsSearchResults, SearchAirports } from 'acscp/actions';
import { InputWithValidation } from 'acscp/components/common/validation/inputWithValidation';
import { ValidationErrors } from 'acscp/api/models/errorDetails';
import { getDisplayTextForAirport } from './helpers/airportHelper';
import { defineMessages, injectIntl } from 'react-intl';
import InjectedIntlProps = ReactIntl.InjectedIntlProps;
import { Ref } from 'react';
import CustomReactPopover from 'acscp/components/common/popover/custom-react-popover';
import { isApplicationRunningOniOS } from 'acscp/components/common/helpers/isApplicationRunningOniOS';

const translations = defineMessages({
    placeholder: {
        id: 'components.flightRequest.form.airportTypeahead.placeholder',
        defaultMessage: 'Type a Location, Airport or Postcode',
    }
});

interface AirportListItemProps {
    airport: Airport;
    isHighlighted: boolean;
    selectedAirportChanged: (airport: Airport) => void;
    setRef: Ref<HTMLLIElement>;
}

const preventBlur = (mouseDownEvent: React.MouseEvent<HTMLLIElement>): void => {
    mouseDownEvent.preventDefault();
};

const AirportListItem: React.SFC<AirportListItemProps> = (props: AirportListItemProps) => {
    const {airport, isHighlighted, selectedAirportChanged} = props;
    return (
        <li
            className={classNames(style.TypeaheadListItem, {[style.Highlighted]: isHighlighted})}
            onMouseDown={preventBlur}
            onClick={() => selectedAirportChanged(airport)}
            key={airport.id}
            ref={props.setRef} // eslint-disable-line
        >
            <div className={style.TypeaheadListItemText}>
                {airport.name}
            </div>
            <div className={style.TypeaheadListItemLabel}>
                {airport.iataCode}
            </div>
        </li>
    );
};

interface AirportListProps {
    airports: Airport[];
    highlightedAirport: Airport | null;
    selectedAirportChanged: (airport: Airport) => void;
}

class AirportList extends React.Component<AirportListProps> {
    private highlightedAirport: HTMLLIElement | null;

    componentDidUpdate(prevProps: Readonly<AirportListProps>, prevState: Readonly<{}>, snapshot?: any): void {
        if (this.props.highlightedAirport !== prevProps.highlightedAirport) {
            if (this.highlightedAirport) {
                this.highlightedAirport.scrollIntoView({block: 'nearest'});
            }
        }
    }

    render(): JSX.Element {
        const {airports, highlightedAirport, selectedAirportChanged} = this.props;

        return (
            <div className={style.TypeaheadResultsPopover}>
                <div className={style.TypeaheadResultsTitle}>
                    Airport suggestions
                </div>
                <ul className={style.TypeaheadResults}>
                {
                    airports.map((airport: Airport) => {
                        const isHighlighted = highlightedAirport !== null &&
                            airport.id.toLowerCase() === highlightedAirport.id.toLowerCase();
                        return(
                            <AirportListItem
                                key={airport.id}
                                airport={airport}
                                isHighlighted={isHighlighted}
                                selectedAirportChanged={selectedAirportChanged}
                                setRef={isHighlighted ? r => this.highlightedAirport = r : null}
                            />
                        );
                    })
                }
                </ul>
            </div>
        );
    }
}

interface AirportTypeaheadOwnProps {
    name: string;
    selectedAirport: Airport | null;
    alternativeLocation: string | null;
    validationErrors: ValidationErrors;
    errorMessagesClassName?: string;
    airportSelectedHandler: (selectedAirport: Airport, name: string) => void;
    inputValueChangedHandler: (inputValue: string, name: string) => void;
}

interface AirportTypeaheadStateProps {
    airports: Airport[];
}

interface AirportTypeaheadDispatchProps {
    searchAirports: (query: string) => void;
    clearAirportsSearch: () => void;
}

interface AirportTypeaheadState {
    typeaheadIsFocused: boolean;
    hasAirportsToDisplay: boolean;
    highlightedAirport: Airport | null;
    inputValue: string;
}

class AirportTypeaheadPresentation extends PresentationComponentBase<
        AirportTypeaheadOwnProps & InjectedIntlProps,
        AirportTypeaheadStateProps,
        AirportTypeaheadDispatchProps,
        AirportTypeaheadState> {
    private airportTypeaheadElement: HTMLDivElement | null = null;

    constructor(props: AirportTypeaheadOwnProps & InjectedIntlProps & AirportTypeaheadStateProps & AirportTypeaheadDispatchProps) {
        super(props);

        this.state = {
            typeaheadIsFocused: false,
            highlightedAirport: null,
            inputValue: this.getInputValueFromProps(props),
            hasAirportsToDisplay: this.props.airports.length > 0
        };
    }

    componentDidUpdate(previousProps: AirportTypeaheadOwnProps & AirportTypeaheadStateProps, previousState: AirportTypeaheadState): void {
        if (this.props.selectedAirport !== previousProps.selectedAirport ||
                this.props.alternativeLocation !== previousProps.alternativeLocation) {
            this.setState({
                inputValue: this.getInputValueFromProps(this.props),
                highlightedAirport: null
            });
        }

        if (this.props.airports !== previousProps.airports) {
            this.setState({
                hasAirportsToDisplay: this.props.airports.length > 0,
                highlightedAirport: null
            });
        }
    }

    getInputValueFromProps = (props: AirportTypeaheadOwnProps) => {
        return getDisplayTextForAirport(props.selectedAirport) || props.alternativeLocation || '';
    }

    selectAirport = (airport: Airport) => {
        this.setState({
            inputValue: getDisplayTextForAirport(airport)
        });

        this.props.airportSelectedHandler(airport, this.props.name);
        this.props.clearAirportsSearch();
    }

    inputValueUpdated = (inputValue: string) => {
        this.props.searchAirports(inputValue);

        this.setState({
            inputValue
        });

        this.props.inputValueChangedHandler(inputValue, this.props.name);
    }

    selectHighlightedAirport = () => {
        if (this.state.highlightedAirport !== null) {
            this.selectAirport(this.state.highlightedAirport);
        }
    }

    onFocus = () => {
        this.props.searchAirports(this.state.inputValue);
        this.setState({typeaheadIsFocused: true});
    }

    onBlur = () => {
        this.setState({typeaheadIsFocused: false});
    }

    onArrowDown = () => {
        if (this.props.airports.length === 0) {
            return;
        }

        const indexToHighlight = this.state.highlightedAirport !== null ?
            this.correctToWithinArrayBoundaries(this.props.airports.indexOf(this.state.highlightedAirport) + 1) : 0;

        this.setState({highlightedAirport: this.props.airports[indexToHighlight]});
    }

    onArrowUp = () => {
        if (this.props.airports.length === 0) {
            return;
        }

        const indexToHighlight = this.state.highlightedAirport !== null ?
            this.correctToWithinArrayBoundaries(this.props.airports.indexOf(this.state.highlightedAirport) - 1) :
            this.props.airports.length - 1;

        this.setState({highlightedAirport: this.props.airports[indexToHighlight]});
    }

    correctToWithinArrayBoundaries = (index: number) => {
        if (index < 0) {
            return index + this.props.airports.length;
        }
        return index % this.props.airports.length;
    }

    onKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
        if (event.key === 'Enter') {
            event.preventDefault(); // Stops the form trying to submit

            // These 'key down' behaviours are designed as interactions with a keyboard on a website.
            // This produces an annoying UX on devices with virtual keyboards _by default_.
            //
            // However, Android replaces the buttons on the keyboard contextually,
            // so instead of a standard 'Enter' button and event we get special (rather nice) behaviour.
            //
            // Still, for iOS we need to fix the problem manually.
            // Currently, we decided to only fix it on the iOS app (and not in any browser)
            // by making it perform the same action as the 'Done' button i.e. hide the keyboard.
            if (isApplicationRunningOniOS()) {
                // When input loses the focus, the keyboard is automatically hidden.
                event.currentTarget.blur();
            } else {
                this.selectHighlightedAirport();
            }
        }
        if (event.key === 'Tab') {
            this.selectHighlightedAirport();
        }
        if (event.key === 'ArrowDown') {
            this.onArrowDown();
        }
        if (event.key === 'ArrowUp') {
            this.onArrowUp();
        }
    }

    render(): JSX.Element {
        const showAirports = this.state.typeaheadIsFocused && this.state.hasAirportsToDisplay;
        return (
            <div
                className={style.Typeahead}
                ref={el => this.airportTypeaheadElement = el}
            >
                <CustomReactPopover
                    isOpen={showAirports}
                    position={'bottom'}
                    align={'start'}
                    transitionDuration={0.1}
                    padding={0}
                    windowBorderPadding={0}
                    content={(
                        <AirportList
                            airports={this.props.airports}
                            highlightedAirport={this.state.highlightedAirport}
                            selectedAirportChanged={this.selectAirport}
                        />
                    )}
                >
                    <InputWithValidation
                        className={style.Input}
                        name={`${this.props.name}Airport`}
                        type="text"
                        autoComplete="off"
                        value={this.state.inputValue}
                        onFocus={this.onFocus}
                        onBlur={this.onBlur}
                        onChange={event => this.inputValueUpdated(event.target.value)}
                        onKeyDown={event => this.onKeyDown(event)}
                        validationErrors={this.props.validationErrors}
                        errorMessagesClassName={this.props.errorMessagesClassName}
                        placeholder={this.props.intl.formatMessage(translations.placeholder)}
                    />
                </CustomReactPopover>
            </div>
        );
    }
}

function mapDispatchToProps(dispatch: Dispatch, ownProps: {}): AirportTypeaheadDispatchProps {
    return {
        searchAirports: (query: string) => dispatch(new SearchAirports(query)),
        clearAirportsSearch: () => dispatch(new ClearAirportsSearchResults())
    };
}

function mapStateToProps(state: AppState, ownProps: AirportTypeaheadOwnProps): AirportTypeaheadStateProps {
    return {
        airports: state.airports.currentResults
    };
}

export const AirportTypeahead = connect(mapStateToProps, mapDispatchToProps)(injectIntl(AirportTypeaheadPresentation));
